import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useHttpClient } from '@bonusly/http-client';

import { GithubAuthorizeSchema, GithubDisconnectSchema, GithubPreferencesSchema, GithubReposResponseSchema, GithubStatusSchema } from '../schemas/github';

const headers = {
	'Key-Inflection': 'camel',
};

const githubKeys = {
	all: ['github'] as const,
	status: () => [...githubKeys.all, 'status'] as const,
	searchRepos: (q: string) => [...githubKeys.all, 'searchRepos', q] as const,
};

const githubPaths = {
	status: () => '/github_connector/status',
	authorize: () => '/github_connector/authorize',
	disconnect: () => '/github_connector/disconnect',
	searchRepos: () => '/github_connector/search_repos',
	preferences: () => '/github_connector/preferences',
};

export const useGithubStatus = () => {
	const http = useHttpClient();
	return useQuery({
		queryKey: githubKeys.status(),
		queryFn: async () => {
			const res = await http.get(githubPaths.status(), { headers });
			return GithubStatusSchema.parse(res.data);
		},
	});
};

export const useGithubAuthorize = () => {
	const http = useHttpClient();
	return useMutation({
		mutationFn: async (returnPath?: string) => {
			const res = await http.get(githubPaths.authorize(), {
				headers,
				params: returnPath ? { return_path: returnPath } : undefined,
			});
			return GithubAuthorizeSchema.parse(res.data);
		},
	});
};

export const useGithubDisconnect = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async () => {
			const res = await http.delete(githubPaths.disconnect(), { headers });
			return GithubDisconnectSchema.parse(res.data);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: githubKeys.all });
		},
	});
};

export const useGithubSearchRepos = (query: string) => {
	const http = useHttpClient();
	return useQuery({
		queryKey: githubKeys.searchRepos(query),
		queryFn: async () => {
			const res = await http.get(githubPaths.searchRepos(), {
				headers,
				params: { q: query },
			});
			return GithubReposResponseSchema.parse(res.data);
		},
		enabled: query.length >= 2,
		keepPreviousData: true,
	});
};

export const useGithubUpdatePreferences = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (prefs: { selected_orgs: string[]; selected_repos: string[] }) => {
			const res = await http.put(githubPaths.preferences(), prefs, { headers });
			return GithubPreferencesSchema.parse(res.data);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: githubKeys.status() });
		},
	});
};
